// frontend_react/src/components/AdminPanel/POSActivityAdminPanel.jsx

import React, { useEffect, useState } from "react";
import { AdminEmptyState, AdminLoadingState } from "../../pages/admin/ui_engine";
import { usePanelActions } from "../../pages/admin/panel_engine/consumer";
import styled from "styled-components";
import {
  createActivity,
  createActivityProduct,
  deleteActivity,
  deleteActivityImport,
  deleteActivityProduct,
  duplicateActivity,
  exportActivityCsv,
  getActivityImports,
  dryRunActivityProductImport,
  getActivityProducts,
  importActivityProducts,
  listActivities,
  previewActivityProductImport,
  listActivityBlueprints,
  updateActivity,
  updateActivityProduct,
} from "../../services/activityApi";
import ActivityCreateForm, {
  EMPTY_ACTIVITY_CREATE_FORM,
  validateActivityCreateForm,
} from "../Activity/ActivityCreateForm";
import CompanyAutocomplete from "../common/CompanyAutocomplete";
import PasswordConfirmationModal from "../common/PasswordConfirmationModal";
import { useAdminScope } from "../../context/AdminScopeContext";
import {
  useAdminConfirmation,
  useAdminPrompt,
} from "../../pages/admin/panel_engine/consumer";
import { AdminButton } from "../../pages/admin/action_engine";
import {
  AdminBackButton,
  AdminNavigationTab,
  AdminNavigationTabs,
} from "../../pages/admin/navigation_engine";

import {
  AdminActions,
  AdminSection,
  AdminSectionTitle,
} from "../../pages/admin/section_engine";

import {
  useWorkspaceNavigation,
} from "../../pages/admin/workspace_engine";

import {
  getStudioRegistrationForDomain,
} from "../../studioRegistry";

const EMPTY_ACTIVITY_FORM = {
  ...EMPTY_ACTIVITY_CREATE_FORM,
};

const EMPTY_PRODUCT_FORM = {
  designation: "",
  sku: "",
  barcode: "",
  description: "",
  unit: "unité",
  supplier_name: "",
  supplier_reference: "",
  purchase_price_ht: 0,
  purchase_price_ttc: 0,
  purchase_vat_rate: 20,
  selling_price_ht: 0,
  selling_price_ttc: 0,
  vat_rate: 20,
  stock_quantity: 0,
  sale_account_code: "",
  purchase_account_code: "",
  vat_account_code: "",
  revenue_label: "",
};

export default function POSActivityAdminPanel() {
  const [activities, setActivities] = useState([]);
  const [activityBlueprints, setActivityBlueprints] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [activityListHidden, setActivityListHidden] = useState(false);
  const [products, setProducts] = useState([]);
  const [imports, setImports] = useState([]);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importDryRun, setImportDryRun] = useState(null);
  const [importApplyResult, setImportApplyResult] = useState(null);
  const [importApplying, setImportApplying] = useState(false);
  const {
    selectedCompanyUuid,
    effectiveCompanyUuid,
    setSelectedActivity: setAdminSelectedActivity,
  } = useAdminScope();
  const confirm = useAdminConfirmation();
  const prompt = useAdminPrompt();

  const {
    openPanel,
  } = useWorkspaceNavigation();

  const [activityForm, setActivityForm] = useState(EMPTY_ACTIVITY_FORM);
  const [editForm, setEditForm] = useState(null);
  const [productForm, setProductForm] = useState(EMPTY_PRODUCT_FORM);
  const [editingProduct, setEditingProduct] = useState(null);

  const [activeDetailTab, setActiveDetailTab] = useState("products");
  const [showCreateActivity, setShowCreateActivity] = useState(false);
  const [showProductForm, setShowProductForm] = useState(false);

  const [loading, setLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [importsLoading, setImportsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [passwordModal, setPasswordModal] = useState({
    open: false,
    activity: null,
    error: "",
  });

  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);

  const selectedProductCount = products.length;

  const setSuccess = (text) => {
    setMessage(text);
    setIsError(false);
  };

  const setFailure = (text) => {
    setMessage(text);
    setIsError(true);
  };

  const loadActivityBlueprints = async () => {
    try {
      const res = await listActivityBlueprints({
        companyUuid: effectiveCompanyUuid,
      });

      const blueprints = res.data.blueprints ?? [];

      setActivityBlueprints(blueprints);

      setActivityForm((prev) => {
        if (prev.activity_type || blueprints.length === 0) {
          return prev;
        }

        return {
          ...prev,
          activity_type: blueprints[0].activity_type || "",
        };
      });
    } catch (err) {
      console.error(
        "Erreur chargement blueprints activités :",
        err
      );
      setActivityBlueprints([]);
      setFailure(
        "Erreur lors du chargement des types d’activité."
      );
    }
  };

  const loadActivities = async () => {
    setLoading(true);
    setMessage("");

    try {
      const res = await listActivities({ companyUuid: selectedCompanyUuid });
      setActivities(res.data.activities ?? []);
    } catch (err) {
      console.error("Erreur chargement activités :", err);
      setFailure("Erreur lors du chargement des activités.");
    } finally {
      setLoading(false);
    }
  };

  const loadImports = async (activityUuid) => {
    if (!activityUuid) return;

    setImportsLoading(true);

    try {
      const res = await getActivityImports(activityUuid, {
        companyUuid: selectedCompanyUuid,
      });
      setImports(res.data.imports ?? []);
    } catch (err) {
      console.error("Erreur chargement historique imports :", err);
      setFailure("Erreur lors du chargement de l’historique d’import.");
    } finally {
      setImportsLoading(false);
    }
  };

  const loadProducts = async (activity) => {
    if (!activity?.uuid) return;

    setActivityListHidden(true);

    setSelectedActivity(activity);
    setEditForm({
      name: activity.name || "",
      activity_type: activity.activity_type || "",
      company_name: activity.company_name || "",
      company_siret: activity.company_siret || "",
      company_uuid: activity.company_uuid || null,
      description: activity.description || "",
      default_vat_rate: activity.default_vat_rate ?? 20,
      sale_account_code: activity.sale_account_code || "",
      purchase_account_code: activity.purchase_account_code || "",
      vat_account_code: activity.vat_account_code || "",
      revenue_label: activity.revenue_label || "",
    });

    setProducts([]);
    setImports([]);
    setProductsLoading(true);
    setMessage("");
    setShowProductForm(false);
    setEditingProduct(null);
    setProductForm(EMPTY_PRODUCT_FORM);

    try {
      const res = await getActivityProducts(activity.uuid, {
        companyUuid: selectedCompanyUuid,
      });

      setProducts(res.data.products ?? []);
      await loadImports(activity.uuid);
    } catch (err) {
      console.error("Erreur chargement produits activité :", err);
      setFailure("Erreur lors du chargement des produits de l’activité.");
    } finally {
      setProductsLoading(false);
    }
  };

  const showActivityList = () => {
    setActivityListHidden(false);
  };

  const resolveStudioRegistrationForActivity = (
    activity
  ) => {
    const blueprint = activityBlueprints.find(
      (candidate) =>
        candidate.activity_type
        === activity?.activity_type
    );

    const domainId =
      blueprint?.domainId;

    if (!domainId) {
      return null;
    }

    return getStudioRegistrationForDomain(
      domainId
    );
  };

  const handleCreateActivity = async () => {
    const validationError =
      validateActivityCreateForm(
        activityForm,
        {
          requireCompanyUuid: true,
        },
      );

    if (validationError) {
      setFailure(validationError);
      return;
    }

    setSaving(true);
    setMessage("");

    try {
      const res = await createActivity(activityForm, {
        companyUuid: selectedCompanyUuid,
      });
      const created = res.data.activity;

      setAdminSelectedActivity(created);
      setActivities((prev) => [created, ...prev]);
      setActivityForm(EMPTY_ACTIVITY_FORM);
      setShowCreateActivity(false);
      setSuccess("Activité créée.");
      await loadProducts(created);

      const studioRegistration =
        resolveStudioRegistrationForActivity(
          created
        );

      if (studioRegistration) {
        openPanel(
          studioRegistration.panelMode,
          "studio",
          {
            activityUuid: created.uuid,
            companyUuid: created.company_uuid,
          }
        );
      }
    } catch (err) {
      console.error("Erreur création activité :", err);
      setFailure(err?.response?.data?.error || "Erreur lors de la création de l’activité.");
    } finally {
      setSaving(false);
    }
  };

  const handleEditChange = (event) => {
    const { name, value } = event.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveActivity = async () => {
    if (!selectedActivity?.uuid || !editForm) return;

    setSaving(true);
    setMessage("");

    try {
      const payload = {
        ...editForm,
        company_uuid: editForm.company_uuid || null,
      };

      const res = await updateActivity(selectedActivity.uuid, payload, {
        companyUuid: selectedCompanyUuid,
      });
      const updated = res.data.activity;

      setSelectedActivity(updated);

      setEditForm({
        name: updated.name || "",
        activity_type: updated.activity_type || "",
        company_name: updated.company_name || "",
        company_siret: updated.company_siret || "",
        company_uuid: updated.company_uuid || null,
        description: updated.description || "",
        default_vat_rate: updated.default_vat_rate ?? 20,
        sale_account_code: updated.sale_account_code || "",
        purchase_account_code: updated.purchase_account_code || "",
        vat_account_code: updated.vat_account_code || "",
        revenue_label: updated.revenue_label || "",
      });

      setActivities((prev) =>
        prev.map((item) => (item.uuid === updated.uuid ? updated : item))
      );

      setSuccess("Activité mise à jour.");
    } catch (err) {
      console.error("Erreur mise à jour activité :", err);
      setFailure(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Erreur lors de la mise à jour de l’activité."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteActivity = (activity) => {
    setPasswordModal({
      open: true,
      activity,
      error: "",
    });
  };

  const confirmDeleteActivity = async (password) => {
    const activity = passwordModal.activity;
    if (!activity?.uuid) return;

    setLoading(true);
    setPasswordModal((prev) => ({ ...prev, error: "" }));

    try {
      await deleteActivity(
        activity.uuid,
        {
          delete_imported_data: true,
          password,
        },
        {
          companyUuid: selectedCompanyUuid,
        }
      );

      setActivities((prev) => prev.filter((item) => item.uuid !== activity.uuid));

      if (selectedActivity?.uuid === activity.uuid) {
        setSelectedActivity(null);
        setProducts([]);
        setImports([]);
        setEditForm(null);
      }

      setPasswordModal({
        open: false,
        activity: null,
        error: "",
      });

      setSuccess("Activité supprimée avec ses données importées.");
    } catch (err) {
      setPasswordModal((prev) => ({
        ...prev,
        error:
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Mot de passe incorrect ou suppression impossible.",
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateActivity = async () => {
    if (!selectedActivity?.uuid) return;

    const name = await prompt({
      title: "Dupliquer l’activité",
      message:
        "Saisissez le nom de la nouvelle activité. Les produits associés seront également dupliqués.",
      label: "Nom de la nouvelle activité",
      defaultValue: `${selectedActivity.name} - copie`,
      confirmLabel: "Dupliquer",
      cancelLabel: "Annuler",
      required: true,
    });

    if (!name) return;

    setLoading(true);
    setMessage("");

    try {
      const res = await duplicateActivity(
        selectedActivity.uuid,
        {
          name,
          duplicate_products: true,
        },
        {
          companyUuid: selectedCompanyUuid,
        }
      );

      const duplicated = res.data.activity;

      setActivities((prev) => [duplicated, ...prev]);
      setSuccess("Activité dupliquée avec ses produits.");
    } catch (err) {
      console.error("Erreur duplication activité :", err);
      setFailure("Erreur lors de la duplication.");
    } finally {
      setLoading(false);
    }
  };

  const handleExportCsv = async () => {
    if (!selectedActivity?.uuid) return;

    try {
      const res = await exportActivityCsv(selectedActivity.uuid, {
        companyUuid: selectedCompanyUuid,
      });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute(
        "download",
        `activity_${selectedActivity.name}.csv`.replaceAll(" ", "_")
      );

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Erreur export CSV :", err);
      setFailure("Erreur lors de l’export CSV.");
    }
  };

  const buildImportFormData = () => {
    if (!importFile) {
      return null;
    }

    const formData = new FormData();
    formData.append("file", importFile);

    return formData;
  };


  const handleImportFile = async (event) => {
    if (!selectedActivity?.uuid) return;

    const file = event.target.files?.[0];

    setImportFile(file || null);
    setImportPreview(null);
    setImportDryRun(null);
    setMessage("");

    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setUploading(true);

    try {      setImportApplyResult(null);

      const res = await previewActivityProductImport(
        selectedActivity.uuid,
        formData,
        {
          companyUuid: selectedCompanyUuid,
        }
      );

      setImportPreview(
        res.data
      );

      setSuccess(
        "Prévisualisation prête. Aucune donnée n’a été modifiée."
      );
    } catch (err) {
      console.error(
        "Erreur prévisualisation import produits :",
        err
      );

      setImportPreview(null);

      setFailure(
        err?.response?.data?.error
        || "Erreur lors de la prévisualisation de l’import."
      );
    } finally {
      setUploading(false);
    }
  };


  const handleImportDryRun = async () => {
    if (
      !selectedActivity?.uuid
      || !importFile
    ) {
      return;
    }

    const formData =
      buildImportFormData();

    if (!formData) {
      return;
    }

    setUploading(true);
    setMessage("");

    try {
      const res = await dryRunActivityProductImport(
        selectedActivity.uuid,
        formData,
        {
          companyUuid: selectedCompanyUuid,
        }
      );

      setImportDryRun(
        res.data
      );

      setSuccess(
        "Simulation terminée. Aucune donnée n’a été modifiée."
      );
    } catch (err) {
      console.error(
        "Erreur dry-run import produits :",
        err
      );

      setImportDryRun(null);

      setFailure(
        err?.response?.data?.error
        || "Erreur lors de la simulation de l’import."
      );
    } finally {
      setUploading(false);
    }
  };


  const applyActivityProductImport = async () => {
    if (
      !selectedActivity?.uuid
      || !importFile
      || !importPreview
      || !importDryRun
    ) {
      setFailure(
        "Prévisualisez puis simulez l’import avant de l’appliquer."
      );
      return;
    }

    const previewSummary =
      importPreview?.summary
      || importPreview?.counts
      || {};

    const dryRunSummary =
      importDryRun?.summary
      || importDryRun?.counts
      || {};

    const summary = {
      create:
        previewSummary.create
        ?? previewSummary.created
        ?? dryRunSummary.create
        ?? dryRunSummary.created
        ?? 0,

      update:
        previewSummary.update
        ?? previewSummary.updated
        ?? dryRunSummary.update
        ?? dryRunSummary.updated
        ?? 0,

      unchanged:
        previewSummary.unchanged
        ?? dryRunSummary.unchanged
        ?? 0,

      conflict:
        previewSummary.conflict
        ?? previewSummary.conflicts
        ?? dryRunSummary.conflict
        ?? dryRunSummary.conflicts
        ?? 0,

      skip:
        previewSummary.skip
        ?? previewSummary.skipped
        ?? dryRunSummary.skip
        ?? dryRunSummary.skipped
        ?? 0,
    };

    if (Number(summary.conflict || 0) > 0) {
      setFailure(
        "L’import contient des conflits non résolus. "
        + "Corrigez-les avant l’application."
      );
      return;
    }

    const confirmed = await confirm({
      title: "Appliquer l’import produits",
      message:
        `Cette opération va appliquer le plan d’import validé.

CREATE : ${summary.create}
UPDATE : ${summary.update}
UNCHANGED : ${summary.unchanged}
CONFLICT : ${summary.conflict}
SKIP : ${summary.skip}

Les créations et mises à jour seront enregistrées dans le catalogue de l’activité.`,
      confirmLabel: "Appliquer l’import",
      cancelLabel: "Annuler",
      tone: "danger",
    });

    if (!confirmed) {
      return;
    }

    const formData = new FormData();

    formData.append(
      "file",
      importFile
    );

    try {
      setImportApplying(true);
      setFailure("");

      const res = await importActivityProducts(
        selectedActivity.uuid,
        formData,
        {
          companyUuid:
            selectedCompanyUuid,
        }
      );

      const result =
        res?.data
        || {};

      setImportApplyResult(
        result
      );

      setMessage(
        "Import appliqué avec succès."
      );

      await loadProducts(
        selectedActivity
      );

      await loadImports(
        selectedActivity.uuid
      );
    } catch (err) {
      console.error(
        "Erreur application import produits :",
        err
      );

      setFailure(
        err?.response?.data?.error
        || "Erreur lors de l’application de l’import."
      );
    } finally {
      setImportApplying(false);
    }
  };

  const openCreateProduct = () => {
    if (!selectedActivity) return;

    setEditingProduct(null);
    setProductForm({
      ...EMPTY_PRODUCT_FORM,
      vat_rate: selectedActivity.default_vat_rate ?? 20,
      purchase_vat_rate: selectedActivity.default_vat_rate ?? 20,
      sale_account_code: selectedActivity.sale_account_code || "",
      purchase_account_code: selectedActivity.purchase_account_code || "",
      vat_account_code: selectedActivity.vat_account_code || "",
      revenue_label: selectedActivity.revenue_label || "",
    });
    setShowProductForm(true);
  };

  const openEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      designation: product.designation || "",
      sku: product.sku || "",
      barcode: product.barcode || "",
      description: product.description || "",
      unit: product.unit || "unité",
      supplier_name: product.supplier_name || "",
      supplier_reference: product.supplier_reference || "",
      purchase_price_ht: product.purchase_price_ht ?? 0,
      purchase_price_ttc: product.purchase_price_ttc ?? 0,
      purchase_vat_rate: product.purchase_vat_rate ?? 20,
      selling_price_ht: product.selling_price_ht ?? 0,
      selling_price_ttc: product.selling_price_ttc ?? 0,
      vat_rate: product.vat_rate ?? 20,
      stock_quantity: product.stock_quantity ?? 0,
      sale_account_code: product.sale_account_code || "",
      purchase_account_code: product.purchase_account_code || "",
      vat_account_code: product.vat_account_code || "",
      revenue_label: product.revenue_label || "",
    });
    setShowProductForm(true);
  };

  const handleProductChange = (event) => {
    const { name, value } = event.target;
    setProductForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveProduct = async () => {
    if (!selectedActivity?.uuid) return;

    if (!productForm.designation) {
      setFailure("La désignation produit est obligatoire.");
      return;
    }

    setSaving(true);
    setMessage("");

    try {
      if (editingProduct?.uuid) {
        const res = await updateActivityProduct(
          selectedActivity.uuid,
          editingProduct.uuid,
          productForm,
          {
            companyUuid: selectedCompanyUuid,
          }
        );

        const updated = res.data.product;

        setProducts((prev) =>
          prev.map((item) => (item.uuid === updated.uuid ? updated : item))
        );

        setSuccess("Produit mis à jour.");
      } else {
        const res = await createActivityProduct(
          selectedActivity.uuid,
          productForm,
          {
            companyUuid: selectedCompanyUuid,
          }
        );
        const created = res.data.product;

        setProducts((prev) => [created, ...prev]);
        setSuccess("Produit créé.");
      }

      setShowProductForm(false);
      setEditingProduct(null);
      setProductForm(EMPTY_PRODUCT_FORM);
    } catch (err) {
      console.error("Erreur sauvegarde produit :", err);
      setFailure("Erreur lors de l’enregistrement du produit.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProduct = async (product) => {
    if (!selectedActivity?.uuid) return;

    const confirmed = await confirm({
      title: "Supprimer le produit ?",
      message: `Supprimer le produit "${product.designation}" ?`,
      confirmLabel: "Supprimer",
      cancelLabel: "Conserver",
      intent: "danger",
    });

    if (!confirmed) return;

    setMessage("");

    try {
      await deleteActivityProduct(
        selectedActivity.uuid,
        product.uuid,
        {
          companyUuid: selectedCompanyUuid,
        }
      );
      setProducts((prev) => prev.filter((item) => item.uuid !== product.uuid));
      setSuccess("Produit supprimé.");
    } catch (err) {
      console.error("Erreur suppression produit :", err);
      setFailure("Erreur lors de la suppression du produit.");
    }
  };

  const handleDeleteImport = async (importRow) => {
    if (!selectedActivity?.uuid) return;

    const confirmed = await confirm({
      title: "Supprimer l’historique ?",
      message: `Supprimer l’historique d’import "${importRow.filename}" ?`,
      confirmLabel: "Supprimer",
      cancelLabel: "Conserver",
      intent: "danger",
    });

    if (!confirmed) return;

    try {
      await deleteActivityImport(
        selectedActivity.uuid,
        importRow.uuid,
        {
          companyUuid: selectedCompanyUuid,
        }
      );
      setImports((prev) => prev.filter((item) => item.uuid !== importRow.uuid));
      setSuccess("Historique d’import supprimé.");
    } catch (err) {
      console.error("Erreur suppression import :", err);
      setFailure("Erreur lors de la suppression de l’historique d’import.");
    }
  };

  usePanelActions([
    {
      key: "create-activity",
      icon: "＋",
      label: "Nouvelle activité",
      onClick: () => setShowCreateActivity((current) => !current),
    },
    {
      key: "refresh-activities",
      icon: "↻",
      label: loading ? "Actualisation en cours" : "Actualiser",
      disabled: loading,
      onClick: loadActivities,
    },
  ]);

  useEffect(() => {
    setSelectedActivity(null);
    setActivityListHidden(false);
    setProducts([]);
    setImports([]);
    setEditForm(null);
    setShowCreateActivity(false);
    setShowProductForm(false);
    setEditingProduct(null);
    loadActivities();
    loadActivityBlueprints();
  }, [selectedCompanyUuid, effectiveCompanyUuid]);

  return (
    <PanelWrapper>
      {message && <PanelMessage $error={isError}>{message}</PanelMessage>}

      {showCreateActivity && (
        <AdminSection
          $compact={Boolean(selectedActivity)}
        >
          <AdminSectionTitle>
            <span>
              Créer une activité
            </span>
          </AdminSectionTitle>

          <ActivityCreateForm
            value={activityForm}
            onChange={setActivityForm}
            blueprints={activityBlueprints}
            saving={saving}
            onSubmit={handleCreateActivity}
            onCancel={() =>
              setShowCreateActivity(false)
            }
            requireCompanyUuid
          />
        </AdminSection>
      )}

    <Grid $activityListHidden={activityListHidden}>
      {!activityListHidden && (
        <AdminSection $compact={Boolean(selectedActivity)}>
          <AdminSectionTitle>
            <span>Activités</span>
            <Counter>{activities.length}</Counter>
          </AdminSectionTitle>

          {loading && <AdminLoadingState message="Chargement des activités..." compact />}

          {!loading && activities.length === 0 && (
            <AdminEmptyState message="Aucune activité créée." compact />
          )}

          <ActivityList>
            {activities.map((activity) => (
              <ActivityCard
                key={activity.uuid}
                $active={selectedActivity?.uuid === activity.uuid}
                $compact={Boolean(selectedActivity)}
                $collapsed={false}
                onClick={() => loadProducts(activity)}
              >
                <ActivityMain $collapsed={false}>
                  <h3>{activity.name}</h3>
                  <p>{activity.activity_type}</p>
                </ActivityMain>

                <ActivityMeta>
                  <span>{activity.company_name || "Société non renseignée"}</span>
                  <small>TVA {activity.default_vat_rate ?? 0}%</small>
                </ActivityMeta>

                <ActionsRow>
                  <AdminButton
                    variant="ghost"
                    fullWidth
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      loadProducts(activity);
                    }}
                  >
                    Fiche
                  </AdminButton>

                  <AdminButton
                    variant="danger"
                    fullWidth
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleDeleteActivity(activity);
                    }}
                  >
                    Supprimer
                  </AdminButton>
                </ActionsRow>
              </ActivityCard>
            ))}
          </ActivityList>
        </AdminSection>
      )}

        <AdminSection $compact={Boolean(selectedActivity)}>
          {!selectedActivity ? (
            <EmptyDetail>
              <h3>Sélectionne une activité</h3>
              <p>
                La fiche activité affichera ses paramètres, ses produits,
                ses imports et son mapping comptable.
              </p>
            </EmptyDetail>
          ) : (
            <>
            {activityListHidden && selectedActivity && (
              <AdminBackButton onClick={showActivityList}>
                {selectedActivity.name}
              </AdminBackButton>
            )}
              <AdminSectionTitle>
                <span>Fiche activité</span>
              </AdminSectionTitle>

              <DetailCard>
                <DetailActions>
                  <h3>{selectedActivity.name}</h3>

                  <ActionsRow>
                    <AdminButton
                      variant="success"
                      fullWidth
                      type="button"
                      onClick={handleSaveActivity}
                      disabled={saving}
                    >
                      {saving ? "Enregistrement..." : "Enregistrer"}
                    </AdminButton>

                    <AdminButton
                      variant="ghost"
                      fullWidth
                      type="button"
                      onClick={handleDuplicateActivity}
                    >
                      Dupliquer
                    </AdminButton>

                    <AdminButton
                      variant="ghost"
                      fullWidth
                      type="button"
                      onClick={handleExportCsv}
                    >
                      Export CSV
                    </AdminButton>
                  </ActionsRow>
                </DetailActions>

                {editForm && (
                  <EditGrid>
                    <Field>
                      <label>Nom activité</label>
                      <input name="name" value={editForm.name} onChange={handleEditChange} />
                    </Field>

                    <Field>
                      <label>Type</label>
                      <input
                        name="activity_type"
                        value={editForm.activity_type}
                        onChange={handleEditChange}
                      />
                    </Field>

                    <CompanyAutocomplete
                      value={editForm.company_name}
                      siret={editForm.company_siret}
                      onChange={(companyData) =>
                        setEditForm((prev) => ({
                          ...prev,
                          ...companyData,
                        }))
                      }
                    />

                    <Field>
                      <label>TVA par défaut</label>
                      <input
                        name="default_vat_rate"
                        type="number"
                        step="0.01"
                        value={editForm.default_vat_rate}
                        onChange={handleEditChange}
                      />
                    </Field>

                    <Field>
                      <label>Compte vente</label>
                      <input
                        name="sale_account_code"
                        value={editForm.sale_account_code}
                        onChange={handleEditChange}
                      />
                    </Field>

                    <Field>
                      <label>Compte achat</label>
                      <input
                        name="purchase_account_code"
                        value={editForm.purchase_account_code}
                        onChange={handleEditChange}
                      />
                    </Field>

                    <Field>
                      <label>Compte TVA</label>
                      <input
                        name="vat_account_code"
                        value={editForm.vat_account_code}
                        onChange={handleEditChange}
                      />
                    </Field>

                    <Field>
                      <label>Libellé comptable</label>
                      <input
                        name="revenue_label"
                        value={editForm.revenue_label}
                        onChange={handleEditChange}
                      />
                    </Field>

                    <Field $full>
                      <label>Description</label>
                      <textarea
                        name="description"
                        value={editForm.description}
                        onChange={handleEditChange}
                      />
                    </Field>
                  </EditGrid>
                )}
              </DetailCard>

              <AdminNavigationTabs aria-label="Navigation de l’activité">
                <AdminNavigationTab
                  active={activeDetailTab === "products"}
                  onClick={() => setActiveDetailTab("products")}
                >
                  Produits ({selectedProductCount})
                </AdminNavigationTab>

                <AdminNavigationTab
                  active={activeDetailTab === "imports"}
                  onClick={() => setActiveDetailTab("imports")}
                >
                  Imports ({imports.length})
                </AdminNavigationTab>
              </AdminNavigationTabs>

              {activeDetailTab === "products" && (
                <>
                  <Toolbar>
                    <UploadLabel>
                      {uploading ? "Analyse en cours..." : "Migration / import initial"}
                      <input
                        type="file"
                        accept=".csv,.xlsx,.json"
                        onChange={handleImportFile}
                        disabled={uploading}
                      />
                    </UploadLabel>

                    <AdminButton
                      variant="secondary"
                      fullWidth
                      type="button"
                      onClick={handleImportDryRun}
                      disabled={
                        uploading
                        || !importFile
                        || !importPreview
                      }
                    >
                      {uploading ? "Simulation..." : "Dry-run"}
                    </AdminButton>

                    <AdminButton
                      variant="success"
                      fullWidth
                      type="button"
                      onClick={openCreateProduct}
                    >
                      Ajouter produit
                    </AdminButton>
                  </Toolbar>

                  {importPreview && (
                    <DetailCard
                      data-testid="activity-product-import-preview"
                    >
                      <DetailActions>
                        <h3>Prévisualisation de l’import</h3>
                      </DetailActions>

                      <pre>
                        {JSON.stringify(
                          importPreview,
                          null,
                          2
                        )}
                      </pre>
                    </DetailCard>
                  )}

                  {importDryRun && (
                    <DetailCard
                      data-testid="activity-product-import-dry-run"
                    >
                      <DetailActions>
                        <h3>Résultat du dry-run</h3>
                      </DetailActions>

                      <pre>
                        {JSON.stringify(
                          importDryRun,
                          null,
                          2
                        )}

                  {importDryRun && (
                    <AdminActions>
                      <AdminButton
                        type="button"
                        onClick={applyActivityProductImport}
                        disabled={
                          importApplying
                          || !importFile
                          || !importPreview
                          || !importDryRun
                        }
                        data-testid="activity-product-import-apply"
                      >
                        {importApplying
                          ? "Application en cours…"
                          : "Appliquer l’import"}
                      </AdminButton>
                    </AdminActions>
                  )}

                  {importApplyResult && (
                    <div
                      data-testid="activity-product-import-result"
                    >
                      <strong>
                        Résultat de l’import
                      </strong>

                      <div>
                        Créés : {importApplyResult.created ?? 0}
                      </div>

                      <div>
                        Mis à jour : {importApplyResult.updated ?? 0}
                      </div>

                      <div>
                        Inchangés : {importApplyResult.unchanged ?? 0}
                      </div>

                      <div>
                        Ignorés : {importApplyResult.skipped ?? 0}
                      </div>

                      <div>
                        Échecs : {importApplyResult.failed ?? 0}
                      </div>

                      <div>
                        Statut : {importApplyResult.status || "—"}
                      </div>

                      {importApplyResult.import_uuid && (
                        <div>
                          Import UUID : {importApplyResult.import_uuid}
                        </div>
                      )}
                    </div>
                  )}

                      </pre>
                    </DetailCard>
                  )}

                  {showProductForm && (
                    <DetailCard>
                      <DetailActions>
                        <h3>{editingProduct ? "Modifier produit" : "Ajouter produit"}</h3>
                      </DetailActions>

                      <ProductForm form={productForm} onChange={handleProductChange} />

                      <ActionsRow>
                        <AdminButton
                          variant="success"
                          fullWidth
                          type="button"
                          onClick={handleSaveProduct}
                          disabled={saving}
                        >
                          {saving ? "Enregistrement..." : "Enregistrer produit"}
                        </AdminButton>

                        <AdminButton
                          variant="ghost"
                          fullWidth
                          type="button"
                          onClick={() => {
                            setShowProductForm(false);
                            setEditingProduct(null);
                          }}
                        >
                          Annuler
                        </AdminButton>
                      </ActionsRow>
                    </DetailCard>
                  )}

                  {productsLoading && <AdminLoadingState message="Chargement des produits..." compact />}

                  {!productsLoading && products.length === 0 && (
                    <AdminEmptyState message="Aucun produit importé sur cette activité." compact />
                  )}

                  {!productsLoading && products.length > 0 && (
                    <ProductsTableWrapper>
                      <ProductsTable>
                        <thead>
                          <tr>
                            <th>Désignation</th>
                            <th>SKU</th>
                            <th>EAN</th>
                            <th>Fournisseur</th>
                            <th>PA HT</th>
                            <th>PV TTC</th>
                            <th>Stock</th>
                            <th>Compta</th>
                            <th>Actions</th>
                          </tr>
                        </thead>

                        <tbody>
                          {products.map((product) => (
                            <tr key={product.uuid}>
                              <td>{product.designation}</td>
                              <td>{product.sku || "—"}</td>
                              <td>{product.barcode || "—"}</td>
                              <td>{product.supplier_name || "—"}</td>
                              <td>{formatMoney(product.purchase_price_ht)}</td>
                              <td>{formatMoney(product.selling_price_ttc)}</td>
                              <td>{product.stock_quantity ?? 0}</td>
                              <td>
                                <CodeText>
                                  {product.sale_account_code || "—"} /{" "}
                                  {product.vat_account_code || "—"}
                                </CodeText>
                              </td>
                              <td>
                                <InlineActions>
                                  <AdminButton
                                    variant="ghost"
                                    size="small"
                                    type="button"
                                    onClick={() => openEditProduct(product)}
                                  >
                                    Modifier
                                  </AdminButton>
                                  <AdminButton
                                    variant="danger"
                                    size="small"
                                    type="button"
                                    onClick={() => handleDeleteProduct(product)}
                                  >
                                    Suppr.
                                  </AdminButton>
                                </InlineActions>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </ProductsTable>
                    </ProductsTableWrapper>
                  )}
                </>
              )}

              {activeDetailTab === "imports" && (
                <>
                  {importsLoading && <AdminLoadingState message="Chargement de l’historique..." compact />}

                  {!importsLoading && imports.length === 0 && (
                    <AdminEmptyState message="Aucun import enregistré." compact />
                  )}

                  {!importsLoading && imports.length > 0 && (
                    <ProductsTableWrapper>
                      <ProductsTable>
                        <thead>
                          <tr>
                            <th>Fichier</th>
                            <th>Type</th>
                            <th>Statut</th>
                            <th>Lignes</th>
                            <th>Date</th>
                            <th>Erreur</th>
                            <th>Actions</th>
                          </tr>
                        </thead>

                        <tbody>
                          {imports.map((row) => (
                            <tr key={row.uuid}>
                              <td>{row.filename}</td>
                              <td>{row.mime_type || "—"}</td>
                              <td>{row.status}</td>
                              <td>{row.imported_rows ?? 0}</td>
                              <td>{formatDate(row.created_at)}</td>
                              <td>{row.error_message || "—"}</td>
                              <td>
                                <AdminButton
                                  variant="danger"
                                  size="small"
                                  type="button"
                                  onClick={() => handleDeleteImport(row)}
                                >
                                  Suppr.
                                </AdminButton>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </ProductsTable>
                    </ProductsTableWrapper>
                  )}
                </>
              )}
            </>
          )}
        </AdminSection>
      </Grid>
      <PasswordConfirmationModal
        open={passwordModal.open}
        title="Suppression d’activité"
        message={`Cette action va supprimer définitivement l’activité "${
          passwordModal.activity?.name || ""
        }" ainsi que ses produits/imports. Veuillez confirmer avec votre mot de passe.`}
        confirmLabel="Supprimer définitivement"
        loading={loading}
        error={passwordModal.error}
        onConfirm={confirmDeleteActivity}
        onCancel={() =>
          setPasswordModal({
            open: false,
            activity: null,
            error: "",
          })
        }
      />
    </PanelWrapper>
  );
}

function ProductForm({ form, onChange }) {
  return (
    <EditGrid>
      <Field>
        <label>Désignation</label>
        <input name="designation" value={form.designation} onChange={onChange} />
      </Field>

      <Field>
        <label>SKU</label>
        <input name="sku" value={form.sku} onChange={onChange} />
      </Field>

      <Field>
        <label>EAN / Code-barres</label>
        <input name="barcode" value={form.barcode} onChange={onChange} />
      </Field>

      <Field>
        <label>Unité</label>
        <input name="unit" value={form.unit} onChange={onChange} />
      </Field>

      <Field>
        <label>Fournisseur</label>
        <input name="supplier_name" value={form.supplier_name} onChange={onChange} />
      </Field>

      <Field>
        <label>Réf. fournisseur</label>
        <input
          name="supplier_reference"
          value={form.supplier_reference}
          onChange={onChange}
        />
      </Field>

      <Field>
        <label>PA HT</label>
        <input
          name="purchase_price_ht"
          type="number"
          step="0.01"
          value={form.purchase_price_ht}
          onChange={onChange}
        />
      </Field>

      <Field>
        <label>PA TTC</label>
        <input
          name="purchase_price_ttc"
          type="number"
          step="0.01"
          value={form.purchase_price_ttc}
          onChange={onChange}
        />
      </Field>

      <Field>
        <label>TVA achat</label>
        <input
          name="purchase_vat_rate"
          type="number"
          step="0.01"
          value={form.purchase_vat_rate}
          onChange={onChange}
        />
      </Field>

      <Field>
        <label>PV HT</label>
        <input
          name="selling_price_ht"
          type="number"
          step="0.01"
          value={form.selling_price_ht}
          onChange={onChange}
        />
      </Field>

      <Field>
        <label>PV TTC</label>
        <input
          name="selling_price_ttc"
          type="number"
          step="0.01"
          value={form.selling_price_ttc}
          onChange={onChange}
        />
      </Field>

      <Field>
        <label>TVA vente</label>
        <input
          name="vat_rate"
          type="number"
          step="0.01"
          value={form.vat_rate}
          onChange={onChange}
        />
      </Field>

      <Field>
        <label>Stock</label>
        <input
          name="stock_quantity"
          type="number"
          step="1"
          value={form.stock_quantity}
          onChange={onChange}
        />
      </Field>

      <Field>
        <label>Compte vente</label>
        <input
          name="sale_account_code"
          value={form.sale_account_code}
          onChange={onChange}
        />
      </Field>

      <Field>
        <label>Compte achat</label>
        <input
          name="purchase_account_code"
          value={form.purchase_account_code}
          onChange={onChange}
        />
      </Field>

      <Field>
        <label>Compte TVA</label>
        <input
          name="vat_account_code"
          value={form.vat_account_code}
          onChange={onChange}
        />
      </Field>

      <Field $full>
        <label>Libellé comptable</label>
        <input name="revenue_label" value={form.revenue_label} onChange={onChange} />
      </Field>

      <Field $full>
        <label>Description</label>
        <textarea name="description" value={form.description} onChange={onChange} />
      </Field>
    </EditGrid>
  );
}

function formatMoney(value) {
  const number = Number(value ?? 0);
  return `${number.toFixed(2)} €`;
}

function formatDate(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString("fr-FR");
}

const PanelWrapper = styled.div`
  display: grid;
  gap: 1rem;
  color: #f7f7f7;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: ${({ $activityListHidden }) =>
    $activityListHidden ? "1fr" : "minmax(280px, 0.85fr) minmax(520px, 1.5fr)"};
  gap: 1rem;
  transition: grid-template-columns 0.28s ease;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Counter = styled.small`
  min-width: 28px;
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
  text-align: center;
  background: rgba(255, 249, 234, 0.12);
  color: #fff9ea;
  font-weight: 950;
`;

const ActivityList = styled.div`
  display: grid;
  gap: 0.75rem;
`;

const ActivityCard = styled.article`
  display: grid;
  place-items: ${({ $collapsed }) => ($collapsed ? "center" : "stretch")};
  gap: ${({ $collapsed }) => ($collapsed ? "0" : "0.6rem")};
  min-height: ${({ $collapsed }) => ($collapsed ? "52px" : "auto")};
  padding: ${({ $collapsed }) => ($collapsed ? "0.55rem" : "0.9rem")};
  border-radius: ${({ $collapsed }) => ($collapsed ? "999px" : "16px")};
  cursor: pointer;
  background: ${({ $active }) =>
    $active ? "rgba(255, 249, 234, 0.18)" : "rgba(255, 255, 255, 0.06)"};
  border: 1px solid
    ${({ $active }) =>
      $active ? "rgba(255, 249, 234, 0.48)" : "rgba(255, 255, 255, 0.08)"};
  transition: all 0.28s ease;

  &:hover {
    background: rgba(255, 249, 234, 0.12);
  }
`;

const ActivityMain = styled.div`
  width: 100%;
  text-align: ${({ $collapsed }) => ($collapsed ? "center" : "left")};

  h3 {
    margin: 0;
    font-size: ${({ $collapsed }) => ($collapsed ? "0.82rem" : "1rem")};
    font-weight: 950;
    white-space: ${({ $collapsed }) => ($collapsed ? "nowrap" : "normal")};
    overflow: hidden;
    text-overflow: ellipsis;
  }

  p {
    margin: 0.25rem 0 0;
    opacity: 0.66;
    font-weight: 750;
  }
`;

const ActivityMeta = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  font-size: 0.8rem;
  opacity: 0.76;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;


const EmptyDetail = styled.div`
  display: grid;
  place-items: center;
  min-height: 320px;
  padding: 1rem;
  text-align: center;
  opacity: 0.75;

  h3 {
    margin: 0 0 0.4rem;
  }

  p {
    max-width: 420px;
    margin: 0;
    line-height: 1.45;
  }
`;

const DetailCard = styled.div`
  display: grid;
  gap: 1rem;
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.06);

  h3 {
    margin: 0;
    color: #fff9ea;
    font-weight: 950;
  }
`;

const DetailActions = styled.div`
  display: grid;
  gap: 0.75rem;

  h3 {
    margin: 0;
    color: #fff9ea;
    font-weight: 950;
  }
`;

const EditGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: grid;
  gap: 0.35rem;
  grid-column: ${({ $full }) => ($full ? "1 / -1" : "auto")};

  label {
    font-size: 0.75rem;
    opacity: 0.72;
    font-weight: 850;
  }

  input,
  textarea,
  select {
    width: 100%;
    min-height: 40px;
    border: none;
    border-radius: 10px;
    padding: 0.7rem;
    background: #fff9ea;
    color: #0b1328;
    font-weight: 750;
    box-sizing: border-box;
    outline: none;
  }

  textarea {
    min-height: 90px;
    resize: vertical;
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const UploadLabel = styled.label`
  flex: 1;
  display: grid;
  place-items: center;
  border-radius: 10px;
  padding: 0.65rem 0.75rem;
  background: #fff9ea;
  color: #0b1328;
  font-weight: 950;
  cursor: pointer;

  input {
    display: none;
  }
`;

const ProductsTableWrapper = styled.div`
  max-height: 520px;
  overflow: auto;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const ProductsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 980px;
  background: rgba(255, 255, 255, 0.04);

  th,
  td {
    padding: 0.7rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    text-align: left;
    font-size: 0.82rem;
  }

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: #0b1328;
    color: #fff9ea;
    font-weight: 950;
  }

  td {
    color: #f7f7f7;
  }
`;

const InlineActions = styled.div`
  display: flex;
  gap: 0.4rem;
`;

const CodeText = styled.code`
  display: block;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.75rem;
  color: #fff9ea;
`;

const PanelMessage = styled.div`
  padding: 0.85rem 1rem;
  border-radius: 12px;
  font-weight: 850;
  background: ${({ $error }) =>
    $error ? "rgba(180, 40, 40, 0.9)" : "rgba(40, 140, 80, 0.9)"};
`;